// Internationalization utils for the application

// Type for all available languages
export type AvailableLanguage = 'en-GB' | 'ja-JP';
// Note: These languages are supported for speech recognition,
// but UI translations are currently only available for 'en-GB' and 'ja-JP'
// export type AvailableLanguage = 'en-GB' | 'ja-JP' | 'en-US' | 'zh-CN' | 'ko-KR' | 'fr-FR' | 'de-DE' | 'es-ES' | 'it-IT' | 'pt-BR' | 'ru-RU';

// Type for all translatable text in the application
export interface TranslationDictionary {
  title: string;
  subtitle: string;
  metaDescription: string;
  recordingReady: string;
  recordingInProgress: string;
  transcriptionPlaceholder: string;
  downloadTranscription: string;
  recordingHistory: string;
  realtimeTranscription: string;
  settings: string;
  aboutApp: string;
  microphoneSelection: string;
  recognitionLanguage: string;
  uiLanguage: string;
  nightMode: string;
  cantChangeWhileRecording: string;
  selectMicrophone: string;
  searchPlaceholder: string;
  clearSearch: string;
  selectAll: string;
  deselectAll: string;
  clearHistory: string;
  deleteSelected: string;
  audioDownload: string;
  transcriptionDownload: string;
  clearHistoryConfirmation: string;
  downloadSelectedRecordingsTitle: string;
  downloadSelectedTranscriptionsTitle: string;
  downloadFilesError: string;
  noTranscriptionInSelection: string;
  noTranscriptionAvailable: string;
  micLabel: string;
  defaultMicrophone: string;
  micPrefix: string;
  recognitionLanguagePrefix: string;
  startRecordingAriaLabel: string;
  stopRecordingAriaLabel: string;
  micAccessError: string;
  recordingStartError: string;
  // Speech recognition availability
  speechRecognitionNotAvailable: string;
  speechRecognitionNotAvailableDesc: string;
  unsupportedMobileBrowserWarning: string;
  unsupportedMobileBrowserWarningDesc: string;
  learnMore: string;
  speechRecognitionNotSupportedInSelector: string;
  recordingHistoryEmpty: {
    noRecordings: string;
    startRecording: string;
    loading: string;
    noMatchingRecordings: string;
  };
  recordingItem: {
    selectRecording: string;
    downloadMP3: string;
    downloadWebM: string;
    downloadTranscription: string;
    delete: string;
    deleteConfirmation: string;
    deleteRecording: string;
    fileName: string;
    fileSize: string;
    length: string;
    convertingToMP3: string;
    mp3ConversionComplete: string;
    noMP3File: string;
    transcription: string;
    noTranscription: string;
    playingMP3: string;
  };
  about: {
    overview: {
      title: string;
      content: string;
    };
    features: {
      title: string;
      items: string[];
    };
    tech: {
      title: string;
      items: string[];
    };
    contact: {
      title: string;
      website: string;
      social: string;
    };
  };
}

// Default translations for English (British)
export const enGB: TranslationDictionary = {
  title: "EasyRec",
  subtitle: "Free Audio Recording & Real-time Transcription",
  metaDescription: "EasyRec is a free web app for voice recording and real-time transcription.",
  recordingReady: "Ready to record",
  recordingInProgress: "Recording...",
  transcriptionPlaceholder: "Transcription will appear here when you start recording",
  downloadTranscription: "Download Transcription",
  recordingHistory: "Recording History",
  realtimeTranscription: "Real-time Transcription",
  settings: "Settings",
  aboutApp: "About",
  microphoneSelection: "Microphone",
  recognitionLanguage: "Recognition Language",
  uiLanguage: "UI Language",
  nightMode: "Night Mode",
  cantChangeWhileRecording: "(Can't change while recording)",
  speechRecognitionNotAvailable: "Speech Recognition Not Available",
  speechRecognitionNotAvailableDesc: "Your browser does not support the Web Speech API required for transcription. Please use Chrome, Edge, or Safari for full functionality.",
  unsupportedMobileBrowserWarning: "Mobile Browser Not Supported",
  unsupportedMobileBrowserWarningDesc: "Speech recognition doesn't work properly in iPhone Safari and Android Chrome due to mobile browser limitations. Please use a desktop browser for best experience.",
  learnMore: "Learn More",
  speechRecognitionNotSupportedInSelector: "Speech recognition is not available in your browser.",
  selectMicrophone: "Select microphone",
  searchPlaceholder: "Search recordings or transcriptions...",
  clearSearch: "Clear search",
  selectAll: "□ Select All",
  deselectAll: "✓ Deselect All",
  clearHistory: "Clear History",
  deleteSelected: "Delete Selected",
  audioDownload: "Download Audio",
  transcriptionDownload: "Download Transcriptions",
  clearHistoryConfirmation: "Are you sure you want to delete all recording history? This action cannot be undone.",
  downloadSelectedRecordingsTitle: "Download selected recordings",
  downloadSelectedTranscriptionsTitle: "Download selected transcriptions",
  downloadFilesError: "An error occurred while downloading files.",
  noTranscriptionInSelection: "The selected items have no transcription text.",
  noTranscriptionAvailable: "There is no transcription text.",
  micLabel: "Mic",
  defaultMicrophone: "Default Microphone",
  micPrefix: "Mic: ",
  recognitionLanguagePrefix: "Transcription: ",
  startRecordingAriaLabel: "Start recording",
  stopRecordingAriaLabel: "Stop recording",
  micAccessError: "Could not access microphone. Please check your settings.",
  recordingStartError: "Failed to start recording.",
  recordingHistoryEmpty: {
    noRecordings: "No recordings found.",
    startRecording: "Start recording to see transcriptions here.",
    loading: "Loading recordings...",
    noMatchingRecordings: "No recordings match your search.",
  },
  recordingItem: {
    selectRecording: "Select recording file",
    downloadMP3: "Download MP3",
    downloadWebM: "Download WebM",
    downloadTranscription: "Download transcription",
    delete: "Delete",
    deleteConfirmation: "Are you sure you want to delete this recording?",
    deleteRecording: "Delete recording",
    fileName: "Filename",
    fileSize: "Size",
    length: "Length",
    convertingToMP3: "Converting to MP3...",
    mp3ConversionComplete: "MP3 conversion complete",
    noMP3File: "No MP3 file available",
    transcription: "Transcription",
    noTranscription: "This recording has no transcription text.",
    playingMP3: "Playing in MP3 format",
  },
  about: {
    overview: {
      title: "Overview",
      content: "EasyRec is an application for recording interviews and meetings with high quality audio, and providing real-time transcription. Recording data is saved in the browser and can be downloaded in MP3 format. No recording data is stored on the server."
    },
    features: {
      title: "Main Features",
      items: [
        "High quality audio recording",
        "Real-time speech recognition and transcription",
        "Audio waveform visualization",
        "Save audio files in MP3 format",
        "Manage recording history"
      ]
    },
    tech: {
      title: "Technical Information",
      items: [
        "React + TypeScript + Vite",
        "Zustand (state management)",
        "Web Speech API (SpeechRecognition)",
        "IndexedDB (data persistence)",
        "Tailwind CSS",
        "99% of the code was generated by Github Copilot Agent. This application is a demonstration of vibe coding."
      ]
    },
    contact: {
      title: "Contact",
      website: "Website: https://nextint.co.jp/",
      social: "Social: https://x.com/tokoroten"
    }
  }
};

// Translations for Japanese
export const jaJP: TranslationDictionary = {
  title: "EasyRec",
  subtitle: "無料 音声録音＆リアルタイム文字起こし",
  metaDescription: "EasyRec は無料で使える音声録音・文字起こしWebアプリです。",
  recordingReady: "録音準備完了",
  recordingInProgress: "録音中...",
  transcriptionPlaceholder: "録音を開始すると文字起こしがここに表示されます。",
  downloadTranscription: "文字起こしをダウンロード",
  recordingHistory: "録音履歴",
  realtimeTranscription: "リアルタイム文字起こし",
  settings: "設定",
  aboutApp: "アプリについて",
  microphoneSelection: "マイク選択",
  recognitionLanguage: "認識言語",
  uiLanguage: "UI言語",
  nightMode: "ナイトモード",
  cantChangeWhileRecording: "(録音中は変更できません)",
  speechRecognitionNotAvailable: "音声認識が利用できません",
  speechRecognitionNotAvailableDesc: "お使いのブラウザは文字起こしに必要なWeb Speech APIをサポートしていません。Chrome、Edge、またはSafariをご利用ください。",
  unsupportedMobileBrowserWarning: "モバイルブラウザ非対応",
  unsupportedMobileBrowserWarningDesc: "iPhone SafariとAndroid Chromeでは音声認識が正常に動作しません。モバイルブラウザの制限のため、デスクトップブラウザでのご利用をお勧めします。",
  learnMore: "詳細",
  speechRecognitionNotSupportedInSelector: "お使いのブラウザは音声認識をサポートしていません。",
  selectMicrophone: "マイクを選択",
  searchPlaceholder: "録音または文字起こしを検索...",
  clearSearch: "検索をクリア",
  selectAll: "□ 全て選択",
  deselectAll: "✓ 全て解除",
  clearHistory: "履歴をクリア",
  deleteSelected: "選択項目を削除",
  audioDownload: "音声ダウンロード",
  transcriptionDownload: "文字起こしダウンロード",
  clearHistoryConfirmation: "すべての録音履歴を削除してもよろしいですか？この操作は元に戻せません。",
  downloadSelectedRecordingsTitle: "選択した録音をダウンロード",
  downloadSelectedTranscriptionsTitle: "選択した文字起こしをダウンロード",
  downloadFilesError: "ファイルのダウンロード中にエラーが発生しました。",
  noTranscriptionInSelection: "選択されたアイテムに文字起こしテキストがありません。",
  noTranscriptionAvailable: "文字起こしテキストがありません。",
  micLabel: "マイク",
  defaultMicrophone: "デフォルトマイク",
  micPrefix: "マイク: ",
  recognitionLanguagePrefix: "認識言語: ",
  startRecordingAriaLabel: "録音開始",
  stopRecordingAriaLabel: "録音停止",
  micAccessError: "マイクへのアクセスができませんでした。設定を確認してください。",
  recordingStartError: "録音の開始に失敗しました。",
  recordingHistoryEmpty: {
    noRecordings: "録音が見つかりませんでした。",
    startRecording: "録音を開始すると、ここに文字起こしが表示されます。",
    loading: "録音を読み込み中...",
    noMatchingRecordings: "検索に一致する録音はありません。",
  },
  recordingItem: {
    selectRecording: "録音ファイルを選択",
    downloadMP3: "MP3をダウンロード",
    downloadWebM: "WebMをダウンロード",
    downloadTranscription: "文字起こしダウンロード",
    delete: "削除",
    deleteConfirmation: "この録音を削除してもよろしいですか？",
    deleteRecording: "録音を削除",
    fileName: "ファイル名",
    fileSize: "サイズ",
    length: "長さ",
    convertingToMP3: "MP3に変換中...",
    mp3ConversionComplete: "MP3変換完了",
    noMP3File: "MP3ファイルはありません",
    transcription: "文字起こし",
    noTranscription: "この録音には文字起こしテキストがありません。",
    playingMP3: "MP3形式で再生中",
  },
  about: {
    overview: {
      title: "概要",
      content: "EasyRec は、インタビューや会議などの音声を高品質で録音し、リアルタイムで文字起こしを行うアプリケーションです。録音データはブラウザ内に保存され、MP3形式でダウンロードできます。サーバには録音データは保存されません。"
    },
    features: {
      title: "主な機能",
      items: [
        "高品質な音声録音",
        "リアルタイム音声認識と文字起こし",
        "音声波形の可視化",
        "MP3形式での音声ファイル保存",
        "録音履歴の管理"
      ]
    },
    tech: {
      title: "技術情報",
      items: [
        "React + TypeScript + Vite",
        "Zustand（状態管理）",
        "Web Speech API (SpeechRecognition)",
        "IndexedDB（データの永続化）",
        "Tailwind CSS",
        "99%のコードは、Github Copilot Agentによって生成されました。このアプリケーションはvibe codingのデモンストレーションです。"
      ]
    },
    contact: {
      title: "お問い合わせ",
      website: "ウェブサイト: https://nextint.co.jp/",
      social: "ソーシャル: https://x.com/tokoroten"
    }
  }
};

// Map language codes to their translation dictionaries
export const translations: Record<AvailableLanguage, TranslationDictionary> = {
  'en-GB': enGB,
  'ja-JP': jaJP
};
